/**
 * CookieBanner — Bandeau de consentement cookies RGPD (CNIL-compliant)
 *
 * Conformité :
 * - Refuser aussi simple qu'accepter (CNIL, recommandation 2020-091)
 * - Pas de pré-cochage (RGPD art. 7, CNIL)
 * - Pas de scroll/navigation = consentement (CNIL)
 * - Possibilité de retirer son consentement à tout moment (lien dans le footer)
 * - PostHog (analytics) chargé UNIQUEMENT après "accepted" + analytics
 * - Cookies strictement nécessaires : toujours actifs (auth, panier), pas soumis au consentement
 */

import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Cookie } from "lucide-react";

const CONSENT_KEY = "lmm_cookie_consent";
const CONSENT_DETAILS_KEY = "lmm_cookie_consent_details";
const CONSENT_VERSION = "v2"; // Bump pour redemander à tous si on change les catégories
const REOPEN_EVENT = "lmm:open-cookie-settings";

function readDetails() {
  try {
    const raw = localStorage.getItem(CONSENT_DETAILS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeDetails(analytics) {
  const payload = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    necessary: true, // toujours vrai (techniquement requis)
    analytics: !!analytics,
  };
  localStorage.setItem(CONSENT_DETAILS_KEY, JSON.stringify(payload));
  // Compatibilité avec l'ancien flag utilisé dans public/index.html
  localStorage.setItem(CONSENT_KEY, analytics ? "accepted" : "refused");
  if (analytics && typeof window !== "undefined" && typeof window.__loadPostHog === "function") {
    window.__loadPostHog();
  }
  // Si l'user retire son consentement analytics, on coupe au mieux
  if (!analytics && typeof window !== "undefined" && window.posthog?.opt_out_capturing) {
    try { window.posthog.opt_out_capturing(); } catch {}
  }
}

/**
 * Helper exporté : déclenche l'ouverture du modal de réglages cookies depuis
 * n'importe quel composant (ex: lien "Gérer les cookies" dans le footer).
 */
export function openCookieSettings() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(REOPEN_EVENT));
  }
}

function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsToggle, setAnalyticsToggle] = useState(false);

  useEffect(() => {
    const details = readDetails();
    if (!details) {
      setVisible(true);
    } else {
      setAnalyticsToggle(!!details.analytics);
    }

    const reopen = () => {
      const current = readDetails();
      setAnalyticsToggle(current ? !!current.analytics : false);
      setShowSettings(true);
      setVisible(false);
    };
    window.addEventListener(REOPEN_EVENT, reopen);
    return () => window.removeEventListener(REOPEN_EVENT, reopen);
  }, []);

  const acceptAll = () => {
    writeDetails(true);
    setAnalyticsToggle(true);
    setVisible(false);
    setShowSettings(false);
  };

  const refuseAll = () => {
    writeDetails(false);
    setAnalyticsToggle(false);
    setVisible(false);
    setShowSettings(false);
  };

  const saveCustom = () => {
    writeDetails(analyticsToggle);
    setVisible(false);
    setShowSettings(false);
  };

  return (
    <>
      {visible && (
        <div
          role="dialog"
          aria-label="Bandeau de consentement aux cookies"
          aria-live="polite"
          className="fixed bottom-0 left-0 right-0 z-[100] bg-white border-t shadow-lg p-4 md:p-6"
        >
          <div className="container mx-auto max-w-5xl flex flex-col gap-4 md:flex-row md:items-center">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-gray-700">
                <p className="font-medium text-gray-900 mb-1">Vos préférences cookies</p>
                <p>
                  Nous utilisons des cookies strictement nécessaires au fonctionnement du site
                  (connexion, panier) et, avec votre accord, des cookies d'analyse anonymisée
                  pour améliorer le service. Aucune donnée n'est revendue.{" "}
                  <a href="/politique-confidentialite" className="text-primary underline">
                    En savoir plus
                  </a>
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)}>
                Personnaliser
              </Button>
              <Button variant="outline" size="sm" onClick={refuseAll}>
                Tout refuser
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                Tout accepter
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Réglages des cookies</DialogTitle>
            <DialogDescription>
              Activez ou désactivez les catégories de cookies. Vous pouvez modifier
              ces réglages à tout moment depuis le pied de page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 border rounded-lg p-4">
              <div>
                <p className="font-medium text-gray-900">Strictement nécessaires</p>
                <p className="text-sm text-gray-600 mt-1">
                  Indispensables au fonctionnement du site : session de connexion,
                  panier de sélection, préférence de thème. Ils ne nécessitent
                  pas de consentement.
                </p>
              </div>
              <Switch checked disabled aria-label="Cookies strictement nécessaires (toujours actifs)" />
            </div>

            <div className="flex items-start justify-between gap-4 border rounded-lg p-4">
              <div>
                <p className="font-medium text-gray-900">Analyse d'audience</p>
                <p className="text-sm text-gray-600 mt-1">
                  Statistiques anonymisées via PostHog (pages consultées, parcours)
                  pour améliorer le service. Données hébergées aux États-Unis avec
                  clauses contractuelles types (RGPD).
                </p>
              </div>
              <Switch
                checked={analyticsToggle}
                onCheckedChange={setAnalyticsToggle}
                aria-label="Activer les cookies d'analyse d'audience"
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={refuseAll} className="sm:mr-auto">
              Tout refuser
            </Button>
            <Button variant="ghost" onClick={() => setShowSettings(false)}>
              Annuler
            </Button>
            <Button onClick={saveCustom} className="bg-primary hover:bg-primary/90 text-white">
              Enregistrer mes choix
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CookieBanner;
